import { Drawer as MaterialDrawer } from "@mui/material";
import type { DrawerProps } from "@mui/material";
import { type MutableRefObject, type ReactNode } from "react";

import type { AviarySize } from "@aviary/types";
import { Direction } from "@aviary/types";
import { OverlayProvider } from "@shared/context/OverlayContext";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import type { DrawerAnalyticTitles } from "@shared/types/OverlayAnalyticTitles";

import * as styles from "./Drawer.styles";

type DrawerSize = Extract<AviarySize, "xsmall" | "small" | "medium" | "large">;

interface Props extends DrawerProps {
  /**
   * Optionally renders the drawer
   */
  isOpen: boolean;
  /**
   * Optionally adds a backdrop behind the drawer
   */
  hasBackdrop: boolean;
  /**
   * Sets the direction the drawer opens from
   */
  opensFrom?: Direction;
  /**
   * A callback for when the user clicks outside a drawer with a backdrop
   */
  onClickOutside?: () => void;
  /**
   * Optionally have the children render in a flex column wrapper
   */
  isFlexColumn?: boolean;
  /**
   * Adds a z-index of 8850
   */
  isMaxIndex?: boolean;
  drawerChildRef?: MutableRefObject<HTMLDivElement>;
  children: ReactNode;
  /**
   * Optionally changes the width of the drawer
   *
   * @default medium
   */
  size?: DrawerSize;
  /**
   * Used only for deep linking drawer to remove min-height
   * @default false
   */
  isDeepLinking?: boolean;
  /**
   * A optional unique value for analytics tracking
   * If not provided, a name will be generated
   * We do not guarantee uniqueness
   */
  analyticsName?: string;
  /**
   * The title of the drawer for analytics tracking, overrides auto-detection
   */
  analyticsTitle: DrawerAnalyticTitles;
  /**
   * The flag that lets us determine if we want to log events or not
   * @default true
   */
  trackOverlayView?: boolean;
  /**
   * Whether to track links and button clicks for the drawer
   *
   * @default false
   */
  trackButtonClicks?: boolean;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/layout/Drawer
 */
const Drawer = ({
  isOpen,
  onClickOutside,
  opensFrom = Direction.RIGHT,
  hasBackdrop,
  children,
  isMaxIndex,
  drawerChildRef,
  size = "medium",
  isFlexColumn,
  isDeepLinking = false,
  analyticsName,
  analyticsTitle,
  onClose,
  trackOverlayView = true,
  trackButtonClicks = false,
  ...rest
}: Props) => {
  const springTransitionSpeed = 200;

  const { tablet } = useBreakpoints();

  const conditionalDrawerStyles = () => {
    return [styles.drawer, isMaxIndex && styles.maxIndexDrawer];
  };

  const anchor: Direction = tablet.lessThan ? Direction.BOTTOM : opensFrom;
  const isFromBottom = opensFrom === Direction.BOTTOM;
  const isFromTopOrBottom = opensFrom === Direction.TOP || isFromBottom;
  const isDeepLinkingDrawer = isDeepLinking && isFromBottom;

  const handleClose: DrawerProps["onClose"] = (event, reason) => {
    if (reason === "backdropClick") {
      onClickOutside?.();
    }

    onClose?.(event, reason);
  };

  const renderDrawer = () => (
    <MaterialDrawer
      transitionDuration={springTransitionSpeed}
      css={conditionalDrawerStyles()}
      data-testid="drawer-wrapper"
      open={isOpen}
      hideBackdrop={!hasBackdrop}
      anchor={anchor}
      role="region"
      {...rest}
      onClose={handleClose}
    >
      <section
        css={[
          isFromTopOrBottom && styles.opensFrom[opensFrom],
          isDeepLinkingDrawer && styles.isDeepLinking,
          !isFromTopOrBottom && styles.opensFrom[opensFrom][size],
          styles.container,
        ]}
      >
        <div
          css={[
            !isDeepLinkingDrawer && styles.fullHeightContainer,
            isFlexColumn && styles.flexColumn,
          ]}
          ref={drawerChildRef}
        >
          {children}
        </div>
      </section>
    </MaterialDrawer>
  );

  if (!trackOverlayView) return renderDrawer();

  return (
    <OverlayProvider
      isOpen={isOpen}
      analyticsName={analyticsName}
      analyticsTitle={analyticsTitle}
      openDirection={opensFrom}
      type="drawer"
      trackButtonClicks={trackButtonClicks}
    >
      {renderDrawer()}
    </OverlayProvider>
  );
};

export { Drawer };
export type { Props as DrawerProps };
